import { useCallback, useEffect, useRef, useState } from 'react';
import { useSendCommand } from '@/store/store';
import { CloseIcon, MicIcon, SendIcon, SparkleIcon, SparkleSmallIcon } from '@/icons';
import { fetchAssistant, type AssistantMessage } from '@/components/AIAssistantModal/assistantData';
import styles from '@/components/AIAssistantModal/AIAssistantModal.module.css';

interface AIAssistantModalProps {
  readonly onClose: () => void;
}

// Safety: if the mic is left listening this long, auto-stop it (send M533).
const ASSISTANT_TIMEOUT_MS = 30_000;

// How often the live conversation file is polled while the mic session is active.
const POLL_INTERVAL_MS = 200;

/**
 * AI Assistant modal opened from the G-code upload bar. The surface is a static visual mock.
 * Two independent lifecycles emit MDI commands: the modal itself (open → M531, close → M530)
 * and the mic button (start → M532, stop → M533). A 30s safety timer auto-stops a mic session
 * left running; closing the modal also stops the mic first.
 */
export function AIAssistantModal({ onClose }: AIAssistantModalProps): JSX.Element {
  const sendCommand = useSendCommand();
  const [listening, setListening] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopMic = useCallback(() => {
    clearTimer();
    sendCommand({ type: 'MDI', command: 'M533' });
    setListening(false);
  }, [clearTimer, sendCommand]);

  const startMic = useCallback(() => {
    sendCommand({ type: 'MDI', command: 'M532' });
    setMessages([]); // drop a prior session's tail before the new one streams in
    setListening(true);
    clearTimer();
    timerRef.current = setTimeout(stopMic, ASSISTANT_TIMEOUT_MS);
  }, [clearTimer, sendCommand, stopMic]);

  const toggleMic = useCallback(() => {
    if (listening) stopMic();
    else startMic();
  }, [listening, startMic, stopMic]);

  // If the modal is dismissed while the mic is live, make sure M533 still goes out.
  const handleClose = useCallback(() => {
    if (listening) stopMic();
    onClose();
  }, [listening, onClose, stopMic]);

  // Modal lifecycle: announce open (M531) on mount, close (M530) on unmount.
  // Also clears any pending mic safety timer on unmount.
  useEffect(() => {
    sendCommand({ type: 'MDI', command: 'M531' });
    return () => {
      sendCommand({ type: 'MDI', command: 'M530' });
      clearTimer();
    };
  }, [sendCommand, clearTimer]);

  // While the mic session is active, poll the live conversation file (written by
  // the machine-side python) and render it. Polling is tied to `listening`, so it
  // stops on M530 / close. (startAssistant clears a prior session's tail first.)
  //
  // Chained setTimeout (not setInterval) so a slow read never lets requests stack
  // up or resolve out of order and rewind the conversation. We also only overwrite
  // with a NON-empty result: the writer may not write atomically, so a poll landing
  // mid-write parses empty — ignoring empties holds the last-known messages instead
  // of blanking the pane (messages only ever grow within a session).
  useEffect(() => {
    if (!listening) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = (): void => {
      fetchAssistant()
        .then((msgs) => {
          if (active && msgs.length > 0) setMessages(msgs);
        })
        .catch(() => {
          // Transient backend hiccup — keep the last-known conversation.
        })
        .finally(() => {
          if (active) timer = setTimeout(poll, POLL_INTERVAL_MS);
        });
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [listening]);

  return (
    <div
      className={styles.aiOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="AI Assistant"
      onClick={handleClose}
    >
      <div className={styles.aiModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.aiHeader}>
          <div className={styles.aiHeaderIcon}>
            <SparkleIcon width={22} height={22} />
            <SparkleSmallIcon width={13.2} height={13.2} className={styles.aiHeaderIconSpark} />
          </div>
          <div className={styles.aiTitleBlock}>
            <div className={styles.aiTitleRow}>
              <span className={styles.aiTitle}>AI Assistant</span>
              <span className={styles.aiBetaBadge}>Beta</span>
            </div>
            <div className={styles.aiStatusRow}>
              <span
                className={`${styles.aiStatusDot} ${listening ? styles.aiStatusDotActive : ''}`}
              />
              <span className={styles.aiStatusText}>{listening ? 'Listening…' : 'Ready'}</span>
            </div>
          </div>
          <button className={styles.aiClose} onClick={handleClose} type="button" aria-label="Close">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.aiDivider} />

        <div className={styles.aiBetaNotice} role="note">
          This feature is in beta and not available yet — voice and chat won't respond.
        </div>

        <div className={styles.aiWelcome}>
          <span className={styles.aiSparkle}>✦</span>
          <span className={styles.aiWelcomeText}>How can I help with your machining process?</span>
        </div>

        <div className={styles.aiConversation}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`${styles.aiMsg} ${
                msg.sender === 'user' ? styles.aiMsgUser : styles.aiMsgAssistant
              }`}
            >
              <div className={styles.aiBubble}>{msg.content}</div>
            </div>
          ))}
        </div>

        <div className={styles.aiVoice}>
          <MicIcon className={styles.aiVoiceIcon} width={14} height={22} />
          <span className={styles.aiVoiceText}>You can speak to the machine.</span>
        </div>

        <div className={styles.aiInputContainer}>
          <div className={styles.aiInputRow}>
            <div className={styles.aiTextInput}>Ask about setup, tools, or machining…</div>
            <button
              type="button"
              className={`${styles.aiMicBtn} ${listening ? styles.aiMicBtnActive : ''}`}
              onClick={toggleMic}
              aria-label={listening ? 'Stop assistant' : 'Start assistant'}
              aria-pressed={listening}
              disabled
            >
              <MicIcon width={16} height={26} />
            </button>
            <div className={styles.aiSendBtn}>
              <SendIcon width={17} height={20} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
